<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="handleCancel">
    <div class="modal-container">
      <div class="modal-header">
        <div class="modal-header__icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="4,7 4,4 20,4 20,7" />
            <line x1="9" y1="20" x2="15" y2="20" />
            <line x1="12" y1="4" x2="12" y2="20" />
          </svg>
        </div>
        <h3 class="modal-header__title">{{ t("text_content") }}</h3>
        <button
          class="modal-header__paste-btn"
          @click="pasteFromClipboard"
          title="Paste from clipboard"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"
            />
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <textarea
          ref="textareaRef"
          v-model="localText"
          class="modal-textarea"
          :placeholder="t('enter_text')"
          rows="8"
          @input="handleInput"
        ></textarea>
      </div>

      <div class="modal-footer">
        <button class="modal-btn modal-btn--secondary" @click="handleCancel">
          {{ t("cancel") }}
        </button>
        <button class="modal-btn modal-btn--primary" @click="handleSave">
          {{ t("save") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, nextTick } from "vue";
import { useI18n } from "../../i18n";

export default {
  name: "TextInputModal",
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    initialText: {
      type: String,
      default: "",
    },
  },
  emits: ["save", "cancel", "input"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const localText = ref("");
    const textareaRef = ref(null);

    watch(
      () => props.isOpen,
      async (isOpen) => {
        if (isOpen) {
          localText.value = props.initialText || "";
          await nextTick();
          if (textareaRef.value) {
            textareaRef.value.focus();
          }
        }
      }
    );

    watch(
      () => props.initialText,
      (text) => {
        if (props.isOpen) {
          localText.value = text || "";
        }
      }
    );

    const handleInput = () => {
      emit("input", localText.value);
    };

    const handleSave = () => {
      emit("save", localText.value);
    };

    const handleCancel = () => {
      if (!localText.value.trim()) {
        emit("cancel", true); // No content
      } else {
        emit("cancel", false); // Has content
      }
    };

    const pasteFromClipboard = async () => {
      try {
        const text = await navigator.clipboard.readText();
        if (text) {
          localText.value = localText.value ? localText.value + text : text;
          emit("input", localText.value);
        }
      } catch (e) {
        console.error("Failed to read clipboard:", e);
      }
    };

    return {
      t,
      localText,
      textareaRef,
      handleInput,
      handleSave,
      handleCancel,
      pasteFromClipboard,
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header__icon {
  color: var(--mia-primary);
  display: flex;
  align-items: center;
}

.modal-header__title {
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.modal-header__paste-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;
}

.modal-header__paste-btn:hover {
  background: #e5e7eb;
  color: var(--mia-primary);
}

.modal-body {
  padding: 24px;
  flex: 1;
  overflow: auto;
}

.modal-textarea {
  width: 100%;
  min-height: 200px;
  padding: 16px;
  font-size: 14px;
  line-height: 1.6;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease;
}

.modal-textarea:focus {
  outline: none;
  border-color: var(--mia-primary);
  box-shadow: 0 0 0 3px rgba(0, 196, 68, 0.1);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
}

.modal-btn {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-btn--secondary {
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
}

.modal-btn--secondary:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.modal-btn--primary {
  background: var(--mia-primary);
  border: none;
  color: white;
}

.modal-btn--primary:hover {
  background: var(--mia-primary-dark);
}
</style>
